import React from 'react';
import { Card, CardContent, Typography, Grid, Divider, Box, Link } from '@mui/material';
import { Recurring } from '../../../types';

const BookingSummary = ({
  bookingType,
  startDate,
  endDate,
  totalDays,
  weeklyAvailability,
  onEditDuration,            // Called when the Duration label is clicked
  onEditDateRange,           // Called when the Date Range label is clicked
  onEditWeeklyAvailability   // Called when the Weekly Availability label is clicked
}) => {
  // Get the name of the day for the start date (e.g., "Monday")
  const startDay = startDate.toLocaleDateString('en-US', { weekday: 'long' });

  // Extract available day names from weeklyAvailability entries (assumes each has a parent with a day property)
  const availableDays = weeklyAvailability
    .filter((slot) => slot.parent)
    .map((slot) => slot.parent.day);

  // Check if the start day is included in the available days
  const isAligned = availableDays.includes(startDay);

  return (
    <Card sx={{ margin: '20px 0', padding: '10px' }}>
      <CardContent>
        {/* Header */}
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Booking Overview
        </Typography>
        <Grid container spacing={2}>
          {/* Duration Section */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              <Link
                component="button"
                onClick={onEditDuration}
                variant="subtitle1"
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                Duration
              </Link>
            </Box>
            <Typography variant="body2">
              {bookingType?.length >= 60
                ? `${Math.floor(bookingType.length / 60)} hour${Math.floor(bookingType.length / 60) > 1 ? 's' : ''} ${bookingType.length % 60 ? `${bookingType.length % 60} min` : ''
                }`
                : `${bookingType.length} min`}
            </Typography>
          </Grid>

          {/* Date Range Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link
                component="button"
                onClick={onEditDateRange}
                variant="subtitle1"
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                Date Range
              </Link>
            </Box>
            <Typography variant="body2">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 0,
                  gap: 0
                }}
              >
                <Typography>
                  {bookingType?.recurring !== Recurring.NONE
                    ? `Every ${bookingType?.recurringInterval} ${bookingType?.recurringInterval > 1 ? `${bookingType?.recurring}s` : bookingType?.recurring
                    }`
                    : "No recurrence"}
                </Typography>
              </Box>
              <Typography>
                {bookingType?.end ? (
                  // When an end date exists, show the full date range along with the number of days
                  <>
                    {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()} ({totalDays} day
                    {totalDays > 1 ? 's' : ''})
                  </>
                ) : (
                  // When no end date exists, indicate it's a continuous meeting (ongoing)
                  <>
                    Continuous Meeting: {startDate.toLocaleDateString()} (ongoing)
                  </>
                )}
              </Typography>
            </Typography>
          </Grid>

          {/* Weekly Availability Section */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 1
              }}
            >
              <Link
                component="button"
                onClick={onEditWeeklyAvailability}
                variant="subtitle1"
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                Weekly Availability
              </Link>
            </Box>
            {/* Show a note if the start day doesn't match any available day */}
            {!isAligned && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Note: Your start day ({startDay}) doesn't match your available days ({availableDays.join(', ')}).
                This may delay matching until the next available day.
              </Typography>
            )}
            {/* Iterate through each availability entry */}
            {weeklyAvailability.map((hour) => {
              const { parent, children } = hour;
              if (parent) {
                return (
                  <div key={parent.id}>
                    {/* Parent row with day and time range */}
                    <Grid container alignItems="center" justifyContent="space-between">
                      <Grid item xs={6}>
                        <Typography variant="body1">{parent.day}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right">
                          {parent.onJob ? `${parent.startTime} - ${parent.endTime}` : 'Closed'}
                        </Typography>
                      </Grid>
                    </Grid>
                    {/* Render child working hours with indentation */}
                    {children && children.length > 0 && (
                      <Grid container sx={{ pl: 2, mt: 0.5 }}>
                        {children.map(child => (
                          <Grid
                            container
                            key={child.id}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Grid item xs={6}>
                              <Typography variant="body2">{child.day}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" align="right">
                                {child.onJob ? `${child.startTime} - ${child.endTime}` : 'Closed'}
                              </Typography>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {/* Divider to separate each day's entry */}
                    <Divider sx={{ my: 1 }} />
                  </div>
                );
              }
              return null;
            })}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BookingSummary;
